import cn from 'classnames';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { openAuthModal } from 'src/store/auth-modal/actions';
import { RootState } from 'src/types/store-types';
import OpenSafeDialog from './components/OpenSafeDialog';
import OpenSafeErrorPopup from './components/OpenSafeErrorPopup';
import OpenSafeSuccessPopup from './components/OpenSafeSuccessPopup';
import SafeCard from './components/SafeCard';
import SafePromoBanner from './components/SafePromoBanner';
import SafePromoHistoryTable from './components/SafePromoHistoryTable';
import SafesShop from './components/SafesShop';
import MyHistoryTable from './components/SafeUserHistoryTable';
import './styles.scss';

const SAFES_DATA = {
  safe1: {
    id: 1,
    name: 'SAFE 1 (NFT)',
    image: '/images/safePromo/safe1.png',
    description: `Safe 1:This safe has an individual password created specially for you.
    By guessing it, you will have Game NFT win. Hurry to guess, as the code will renew every 24h`,
    lastRenew: {
      date: '2025-05-05T12:00:00Z',
      time: '12:00',
    },
    passwordChangeTime: '2025-05-25T12:00:00Z',
  },
  safe2: {
    id: 2,
    name: 'SAFE 2 (50.000 AMD)',
    image: '/images/safePromo/safe2.png',
    description: `Safe 2: This safe has an individual password created specially for you.
    By guessing it, you will have 50,000 AMD win. Hurry to guess, as the code will renew every 48h.`,
    lastRenew: {
      date: '2025-05-03T12:00:00Z',
      time: '13:00',
    },
    passwordChangeTime: '2025-05-20T12:00:00Z',
  },
  safe3: {
    id: 3,
    name: 'VBET SAFE (1.000.000 AMD)',
    image: '/images/safePromo/safe3.png',
    description: `Safe 3: This safe's password is mutual for all players.
    By guessing it, you will have 1,000,000 AMD win. The password will renew once someone from Players will guess it.`,
    lastRenew: {
      date: '2025-05-04T12:00:00Z',
      time: '14:00',
    },
    passwordChangeTime: '2025-05-20T12:00:00Z',
  },
};

/* eslint-disable sonarjs/no-duplicate-string */
const HISTORY_DATA = [
  {
    id: 1,
    name: 'SAFE PROMO',
    safeKey: 'safe3',
    date: '29.04.2025',
    time: '12:51 PM',
    code: '662054',
    status: 'fail',
    prize: '',
  },
  {
    id: 2,
    name: 'SAFE 2',
    safeKey: 'safe2',
    date: '28.04.2025',
    time: '12:50 PM',
    code: '655',
    status: 'fail',
    prize: '',
  },
  {
    id: 3,
    name: 'SAFE 1',
    safeKey: 'safe1',
    date: '27.04.2025',
    time: '12:44 PM',
    code: '41',
    status: 'success',
    prize: '',
  },
  {
    id: 4,
    name: 'SAFE PROMO',
    safeKey: 'safe3',
    date: '26.04.2025',
    time: '11:12 AM',
    code: '207116',
    status: 'fail',
    prize: '',
  },
  {
    id: 5,
    name: 'SAFE 2',
    safeKey: 'safe2',
    date: '25.04.2025',
    time: '11:12 AM',
    code: '108',
    status: 'fail',
    prize: '',
  },
  {
    id: 6,
    name: 'SAFE 1',
    safeKey: 'safe1',
    date: '24.04.2025',
    time: '11:11 AM',
    code: '20',
    status: 'fail',
    prize: '',
  },
  {
    id: 7,
    name: 'SAFE PROMO',
    safeKey: 'safe3',
    date: '23.04.2025',
    time: '07:50 AM',
    code: '907543',
    status: 'fail',
    prize: '',
  },
  {
    id: 8,
    name: 'SAFE 2',
    safeKey: 'safe2',
    date: '22.04.2025',
    time: '07:50 AM',
    code: '338',
    status: 'fail',
    prize: '',
  },
  {
    id: 9,
    name: 'SAFE 1',
    safeKey: 'safe1',
    date: '21.04.2025',
    time: '07:50 AM',
    code: '69',
    status: 'fail',
    prize: '',
  },
];

type IOpenSafeDialogDataType = {
  safeName: 'safe1' | 'safe2' | 'safe3';
  safeData: any;
};

const SafePromo: FC = () => {
  const { t }: Translation = useTranslation();

  const _openAuthModal = useConnectedAction(openAuthModal);

  const user = useSelector((state: RootState) => state.user?.user);

  // States
  const [openSafeDialog, setOpenSafeDialog] = useState<boolean>(false);
  const [openSafeErrorDialog, setOpenSafeErrorDialog] = useState<boolean>(false);
  const [openSafeSuccessDialog, setOpenSafeSuccessDialog] = useState<boolean>(false);
  const [safeDialogData, setSafeDialogData] = useState<IOpenSafeDialogDataType | null>(null);
  const [selectedTab, setSelectedTab] = useState<'myHistory' | 'safePromoWinnings'>('safePromoWinnings');

  const onOpenSafeDialogHandler = (safe: 'safe1' | 'safe2' | 'safe3'): void => {
    if (user && safe) {
      setSafeDialogData({
        safeName: safe,
        safeData: SAFES_DATA[safe],
      });
      setOpenSafeDialog(true);
    } else {
      _openAuthModal('login');
    }
  };

  const onCloseSafeDialogHandler = (): void => {
    setOpenSafeDialog(false);
    setSafeDialogData(null);
  };

  const onOpenSafeHandler = (safe: 'safe1' | 'safe2' | 'safe3', code: string): void => {
    console.log(safe, code);
    onCloseSafeDialogHandler();
    if (code.includes('22')) {
      setOpenSafeSuccessDialog(true);
    } else {
      setOpenSafeErrorDialog(true);
    }
  };

  const onTabChangeHandler = (tab: 'myHistory' | 'safePromoWinnings'): void => {
    setSelectedTab(tab);
  };

  return (
    <>
      <div className="page_container">
        <div className="safe_promo_container">
          <SafePromoBanner />
          <section className="safe_cards">
            <div className="safe_cards_container">
              <div className="safe_cards_header">
                <h3 className="safe_cards_title">{t('mySafes')}</h3>
              </div>
              <div className="safe_cards_body">
                <SafeCard data={SAFES_DATA.safe1} onOpenSafe={() => onOpenSafeDialogHandler('safe1')} />
                <SafeCard data={SAFES_DATA.safe2} onOpenSafe={() => onOpenSafeDialogHandler('safe2')} />
              </div>
            </div>
            <div className="safe_cards_container">
              <div className="safe_cards_header">
                <h3 className="safe_cards_title">{t('promoSafe')}</h3>
              </div>
              <div className="safe_cards_body safe_cards_body_promo">
                <SafeCard data={SAFES_DATA.safe3} onOpenSafe={() => onOpenSafeDialogHandler('safe3')} />
              </div>
            </div>
          </section>
          <section className="safe_promo_buy_now_section">
            <SafesShop />
          </section>
          <section className="safe_history">
            <div className="safe_history_tabs">
              {!!user && (
                <button
                  className={cn('safe_history_tab_button', {
                    safe_history_tab_button_active: selectedTab === 'myHistory',
                  })}
                  onClick={() => onTabChangeHandler('myHistory')}
                >
                  {t('safe_promo.myHistory')}
                </button>
              )}
              <button
                className={cn('safe_history_tab_button', {
                  safe_history_tab_button_active: selectedTab === 'safePromoWinnings',
                })}
                onClick={() => onTabChangeHandler('safePromoWinnings')}
              >
                {t('safe_promo.safePromoWinnings')}
              </button>
            </div>
            <div className="safe_history_table_wrapper">
              {selectedTab === 'myHistory' ? (
                <MyHistoryTable data={HISTORY_DATA} />
              ) : (
                <SafePromoHistoryTable data={[]} />
              )}
            </div>
          </section>
        </div>
      </div>
      <OpenSafeDialog
        safeName={safeDialogData?.safeName || ''}
        safeData={safeDialogData?.safeData}
        isOpen={openSafeDialog}
        onClose={onCloseSafeDialogHandler}
        onOpenSafe={onOpenSafeHandler}
      />
      <OpenSafeErrorPopup isOpen={openSafeErrorDialog} onClose={() => setOpenSafeErrorDialog(false)} />
      <OpenSafeSuccessPopup isOpen={openSafeSuccessDialog} onClose={() => setOpenSafeSuccessDialog(false)} />
    </>
  );
};

export default SafePromo;
